import { ChevronDownIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  HStack,
  Text,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Tooltip,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';

type AssignmentEditColumnMultiSelectButtonWithTooltipProps = {
  buttonLabel: string;
  tooltipLabel: string;
  onClick: () => void;
};
const AssignmentEditColumnMultiSelectButtonWithTooltip = ({
  buttonLabel,
  tooltipLabel,
  onClick,
}: AssignmentEditColumnMultiSelectButtonWithTooltipProps) => {
  return (
    <Button
      h="1.75rem"
      size="sm"
      w="90%"
      mt="15px"
      variant="outline"
      fontWeight="normal"
      onClick={() => onClick()}
    >
      {buttonLabel}
      <Tooltip label={tooltipLabel} hasArrow placement="left">
        <InfoOutlineIcon ml="5px" height="12px" />
      </Tooltip>
    </Button>
  );
};

type AssignmentEditColumnMultiSelectWithResetProps = {
  showColumnsInAllTables: (columnIndices: string[]) => void;
  options: { label: string; value: string }[];
  initialSelections: string[];
  resetColumnSettings: () => void;
  eligibilityBaseFieldsColumnIndices?: string[];
};
export const AssignmentEditColumnMultiSelectWithReset = ({
  showColumnsInAllTables,
  options,
  initialSelections,
  resetColumnSettings,
  eligibilityBaseFieldsColumnIndices,
}: AssignmentEditColumnMultiSelectWithResetProps) => {
  const [selectedColumnIndices, setSelectedColumnIndices] = useState<string[]>(
    initialSelections,
  );

  const onOptionChangeHandler = useCallback(
    (value: string) => {
      const updatedOptions = [...selectedColumnIndices];
      const index = updatedOptions.indexOf(value);
      if (index >= 0) {
        updatedOptions.splice(index, 1);
      } else {
        updatedOptions.push(value);
      }
      setSelectedColumnIndices(updatedOptions);
    },
    [selectedColumnIndices],
  );

  return (
    <Box alignItems="right" zIndex={200}>
      <Menu
        matchWidth
        autoSelect={false}
        eventListeners={{ scroll: false, resize: true }}
      >
        {({ onClose }) => (
          <>
            <MenuButton
              as={Button}
              variant="outline"
              minWidth="250px"
              minHeight="40px"
              w="100%"
            >
              <HStack justifyContent="space-between">
                <Text style={{ fontWeight: 400 }}>Select columns</Text>
                <ChevronDownIcon />
              </HStack>
            </MenuButton>
            <MenuList maxHeight="700px">
              {/* do not remove pb here which makes room for stick footer buttons */}
              <Box maxHeight="600px" overflowY="scroll" pb="145px">
                <MenuOptionGroup
                  defaultValue={selectedColumnIndices}
                  value={selectedColumnIndices}
                  title="Available fields"
                  type="checkbox"
                >
                  {options.map(({ label, value }) => {
                    return (
                      <MenuItemOption
                        key={value}
                        value={value}
                        onClick={() => onOptionChangeHandler(value)}
                        closeOnSelect={false}
                      >
                        {label}
                      </MenuItemOption>
                    );
                  })}
                </MenuOptionGroup>
              </Box>
              {/* sticky footer button group */}
              <Box
                pb="10px"
                position="absolute"
                bottom="5px"
                background="white"
              >
                <Divider width="90%" margin="5px auto" />
                <AssignmentEditColumnMultiSelectButtonWithTooltip
                  buttonLabel="Select eligibility-related fields"
                  tooltipLabel="Display all columns for fields that are used in eligibility calculations"
                  onClick={() => {
                    if (eligibilityBaseFieldsColumnIndices) {
                      setSelectedColumnIndices(
                        eligibilityBaseFieldsColumnIndices,
                      );
                    }
                  }}
                />
                <AssignmentEditColumnMultiSelectButtonWithTooltip
                  buttonLabel="Apply columns"
                  tooltipLabel="Display selected columns"
                  onClick={() => {
                    showColumnsInAllTables(selectedColumnIndices);
                    onClose();
                  }}
                />
                <AssignmentEditColumnMultiSelectButtonWithTooltip
                  buttonLabel="Reset columns"
                  tooltipLabel="Reset column settings to default. Your proposed owner selections will
                  not reset"
                  onClick={() => {
                    resetColumnSettings();
                    setSelectedColumnIndices([]);
                    onClose();
                  }}
                />
              </Box>
            </MenuList>
          </>
        )}
      </Menu>
    </Box>
  );
};
